import React, { Dispatch, SetStateAction, useState } from "react";
import { Box, Flex } from "rebass/styled-components";
import styled from "styled-components";
import { Divider } from "../../../../../Components/Divider";
import {
  Title2,
  Headline,
  Subheader,
  Footnote,
  Body,
} from "../../../../../Components/Text";
import { HiOutlineLightBulb, HiPlus } from "react-icons/hi";
import { CardsList } from "./CardsList";
import { useTranslation } from "react-i18next";

import { RemixHeader } from "../../../RemixView/Components/Pinned";
import { StudioSession } from "../../../../../State/studioSession";
import { useRecoilState } from "recoil";
import { useIsMobileApp } from "../../../../../Hooks/useIsMobileApp";
import { EnableSignature } from "../EnableSignature";
import { SignatureSelectorState } from "../../../../../State/loadoutSelector";
import { CreateSignature } from "../CreateSignature";
import { useUser } from "../../../../../Hooks/useUser";

interface IEnabledCardsPanelProps {
  onQuery: (checked: boolean, indentifier: string, label: string) => void;
  isEnabledVisible: boolean;
  setIsEnabledVisable?: Dispatch<SetStateAction<boolean>>;
  handleCardClick: (card: any) => void;
  handleToggle: (card: any, isToggled: any) => void;
  studioSession: any;
  remixData: any;
  postRemix: any;
  ownerInfo: any;
  setModal: any;
}

export const EnabledCardsPanel = ({
  isEnabledVisible,
  setIsEnabledVisable,
  onQuery,
  handleCardClick,
  handleToggle,
  setModal,
  remixData,
  ownerInfo,
  postRemix,
}: IEnabledCardsPanelProps) => {
  const { t } = useTranslation("translation", {
    keyPrefix: "studio",
  });
  const [studioSession] = useRecoilState(StudioSession);
  const [signatureSession] = useRecoilState(SignatureSelectorState);
  const { isMobileApp } = useIsMobileApp();
  const isClearButtonDisabled = studioSession?.userActivated?.length === 0;
  const { allstarUser } = useUser();
  const EnabledCardsNote = () => {
    if (studioSession.isRemixSession) return <></>;
    return (
      <>
        <StyledRemix>
          <Flex mr={4} mt={3} flexDirection="column">
            <Flex flexWrap="wrap">
              <StyledIcon size={20} />
              <Flex ml={2} mb={2} flexDirection="column">
                <StyledSubheader> {t("disclaimerHeader")} </StyledSubheader>
                <Flex
                  width={["120px", "120px", "120px", "120px"]}
                  flexWrap={"wrap"}
                >
                  <StyledSubheaderSmall>
                    {t("disclaimerBody")}
                  </StyledSubheaderSmall>
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </StyledRemix>
      </>
    );
  };

  return (
    <>
      <StickyStudioNav isVisible={isEnabledVisible}>
        <StudioNavContent isVisible={isEnabledVisible}>
          <Box>
            <Flex
              mt={6}
              mb={3}
              justifyContent="space-between"
              alignItems={"center"}
            >
              <StyledTitle2> {t("enabledCards")} </StyledTitle2>
              <StyledClear
                onClick={() => {
                  if (!isClearButtonDisabled) setModal({ type: "clear" });
                }}
                isClearButtonDisabled={isClearButtonDisabled}
              >
                {t("clear")}
              </StyledClear>
            </Flex>

            <>
              {!signatureSession.hasASignature || !allstarUser?.user ? (
                <CreateSignature />
              ) : (
                <EnableSignature />
              )}
            </>
            <Divider />
            <Flex textAlign="center" width="100%" alignItems={"center"} mb={4}>
              <Body color="chalk" style={{ fontWeight: 500 }}>
                {studioSession.isRemixSession
                  ? "Cards added here will replace similar signature cards"
                  : "Cards added here can replace similar signature cards."}
              </Body>
            </Flex>
          </Box>
          <StyledHeaderSection>
            <EnabledCardsNote />
            <CardsList
              onQuery={onQuery}
              handleCardClick={handleCardClick}
              handleToggle={handleToggle}
              userActivated={studioSession.userActivated}
              isRemix={studioSession.isRemixSession}
            />
            {!isMobileApp && studioSession.isRemixSession && (
              <RemixHeader
                remixData={remixData}
                ownerInfo={ownerInfo}
                remixedCards={
                  studioSession.requestingAPI || studioSession.userActivated
                }
                postRemix={postRemix}
              />
            )}
          </StyledHeaderSection>
        </StudioNavContent>
      </StickyStudioNav>
    </>
  );
};

const StyledIcon = styled(HiOutlineLightBulb)<any>`
  display: flex;
  color: ${(props: any) => props.theme.colors.turbo};
`;

const StyledLoadIcon = styled(HiPlus)<any>`
  display: flex;
  color: ${(props: any) => props.theme.colors.chalk};
  &:hover {
    color: inherit;
  }
`;

const StyledSubheader = styled(Footnote)<any>`
  font-weight: 600;
  display: flex;
  width: 100%;
`;

// const StyledCollection = styled(Flex)<any>`
//   align-items: center;
//   flex-direction: row;
//   color: ${(props: any) => props.theme.colors.chalk};
// `;

const StyledSubheaderSmall = styled(Footnote)<any>`
  color: ${(props: any) => props.theme.colors.darkerIceAlpha90};
  display: flex;
  width: 100%;
  flex-wrap: wrap;
`;

const StyledRemix = styled(Flex)`
  position: fixed;
  bottom: 0;
  margin: auto;
  width: 100%;
  height: 100px;
  right: 0;
  z-index: 20;
  padding: 15px;
  border-radius: 0;
  border-top: 2px solid;
  border-color: ${(props: any) => props.theme.colors.navy};
  background-color: ${(props: any) => props.theme.colors.midnight};
  color: ${(props: any) => props.theme.colors.ice};
  @supports (backdrop-filter: blur(10px)) {
    & {
      background-color: #14181ecc;
      backdrop-filter: blur(10px);
    }
  }
`;

const StyledLoadButton = styled(Flex)`
  justify-content: space-between;
  align-items: center;
  margin: auto;
  width: 100%;
  height: 40px;
  color: ${(props: any) => props.theme.colors.chalk};
  &:hover {
    cursor:pointer;
    color: ${(props: any) => props.theme.colors.ice};
  }
  &:hover ${StyledLoadIcon} {
    color: ${(props) => props.theme.colors.envy};
`;

const StyledHeaderSection = styled(Headline)`
  font-weight: 500;
  text-overflow: ellipsis;
  white-space: nowrap;
  padding-bottom: 160px;
`;

const StickyStudioNav = styled(Flex)<any>`
  flex-direction: column;
  width: 100%;
  max-width: ${({ isVisible }) => (isVisible ? "280px" : "0")};
  background: ${({ isVisible }) => (isVisible ? "#1F2D38" : "transparent")};
  transform: ${({ isVisible }) =>
    isVisible ? "translateX(0)" : "translateX(0)"};
  height: calc(100vh - 60px);
  text-align: left;
  padding: 16px;
  position: ${({ isVisible }) => (isVisible ? "sticky" : "fixed")};
  top: 60px;
  box-shadow: ${({ isVisible }) =>
    isVisible ? "0px 3px 10px rgb(12, 19, 24, 0.3)" : "none"};
  right: 0;
  transition: transform 0.3s ease-in-out;
  z-index: ${({ isVisible }) => (isVisible ? 18 : 0)};
  @media (max-width: ${({ theme }) => theme.breaks.medium}) {
    position: ${({ isVisible }) => (isVisible ? "fixed" : "fixed")};
    max-width: ${({ isVisible }) => (isVisible ? "97%" : "0")};
  }
`;

// const NavOverlay = styled(Box)<any>`
//   @media (max-width: 576px) {
//     position: absolute;
//     width: 100vw;
//     height: 100%;
//     background: ${(props: any) => props.theme.colors.midnightAlpha50};
//     opacity: 0;
//     visibility: hidden;
//     overflow: auto;
//     transition: opacity 0.3s ease-in-out, visibility 0.3s ease-in-out;
//     visibility: ${(props: any) => (props.isVisible ? "visible" : "hidden")};
//     ${(props: any) => (props.isVisible ? "opacity: 1" : null)};
//     top: 60px;
//     left: 0;
//     transition: opacity 0.2s ease-in-out;
//     z-index: ${({ isVisible }) => (isVisible ? 19 : 0)};
//   }
// `;

const StudioNavContent = styled(Flex)<any>`
  flex-direction: column;
  opacity: 0;
  visibility: hidden;
  overflow: auto;
  transition: opacity 200ms ease, visibility 200ms ease 0s;
  visibility: ${(props: any) => (props.isVisible ? "visible" : "hidden")};
  ${(props: any) => (props.isVisible ? "opacity: 1" : null)};

  ::-webkit-scrollbar {
    display: none;
  }

   {
    -ms-overflow-style: none;
    scrollbar-width: none;
  }
`;

const StyledTitle2 = styled(Title2)`
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const StyledClear = styled<any>(Flex)`
  padding: 8px 12px;
  background-color: ${(props: any) => {
    return props.theme.colors.darkerNed;
  }};

  border-radius: 999px;
  transition: color 100ms ease;
  font-size: 12px;
  font-weight: 600;

  ${(props) => {
    if (props.isClearButtonDisabled) return "opacity: .5;";
  }}
  color: ${(props: any) => {
    return props.theme.colors.chalk;
  }};
  &:hover {
    ${(props: any) => {
      if (props.isClearButtonDisabled)
        return `
      cursor:default;

      `;
      else
        return `cursor: pointer;
      color: ${props.theme.colors.envy};
      background-color: ${props.theme.colors.envyAlpha10}
      `;
    }}
  }
`;
